import { ActionIcon, Menu, Skeleton, Stack } from '@mantine/core';
import { createReference, getReferenceString, resolveId } from '@medplum/core';
import { Coding, Organization, Patient, PatientContact } from '@medplum/fhirtypes';
import { useMedplum } from '@medplum/react';
import { IconBuildingSkyscraper, IconPlus, IconUsers, IconX } from '@tabler/icons-react';
import { useState, useCallback, useEffect, useMemo, useReducer } from 'react';
import {
  canCreateResourceType,
  canDeleteResourceType,
  canSearchResourceType,
  canUpdateResourceType,
} from '@/services/permissions';
import { useSubscriptionWithLimitHandling } from '@/services/subscriptions';
import { useTimelineContext } from '../../hooks/useTimelineContext';
import { useRelatedPersonManagement } from '../../hooks/useRelatedPersonManagement';
import { useRelatedPersonList } from '../../context-hooks/useRelatedPersonList';
import { usePatientContact } from '../../context-hooks/usePatientContact';
import { InfoCard } from '../shared/InfoCard';
import { RelatedContactList } from './RelatedContactList';
import { RelatedContactForm } from './forms/RelatedContactForm';
import { RelatedContactFormData } from './forms/types';
import { formatHumanName } from '@medplum/core';
import { AddRelatedPartnerForm } from './forms/AddRelatedPartnerForm';
import { ContactPermissions, contactActions, readPolicyholderIds } from './services';

export type PatientContactWithRelatedPerson = PatientContact & {
  id: string;
  relatedPersonId?: string;
};

/** A policyholder read, and the related persons it was taken for. Any other is still unknown. */
interface PolicyholderRead {
  checked: Set<string>;
  policyholders: Set<string> | undefined;
}

export function RelatedContactsCard() {
  const medplum = useMedplum();
  const { patientContext } = useTimelineContext();
  const patient = patientContext?.patient;
  const updatePatient = patientContext?.updatePatient;
  const { relatedPersonList, relatedPersonListLoading } = useRelatedPersonList(patient);
  const { partnersList, partnersListLoading } = usePatientContact(patient);
  const { createRelatedPerson, updateRelatedPerson, deleteRelatedPerson } = useRelatedPersonManagement();

  const permissions = useMemo<ContactPermissions & { createRelatedPerson: boolean }>(
    () => ({
      createRelatedPerson: canCreateResourceType(medplum, 'RelatedPerson'),
      updateRelatedPerson: canUpdateResourceType(medplum, 'RelatedPerson'),
      deleteRelatedPerson: canDeleteResourceType(medplum, 'RelatedPerson'),
      updatePatient: canUpdateResourceType(medplum, 'Patient'),
    }),
    [medplum]
  );

  // Read again when the related persons or the patient's Coverages change. bariendo-be writes a
  // new policyholder before the Coverage naming it, so the related person alone can arrive first.
  // Keyed on the reference, not the Patient object, which every Patient write replaces.
  const patientRef = patient ? getReferenceString(patient) : undefined;
  const [coverageChanges, countCoverageChange] = useReducer((n: number) => n + 1, 0);
  useSubscriptionWithLimitHandling(
    patientRef && canSearchResourceType(medplum, 'Coverage')
      ? `Coverage?${new URLSearchParams({ beneficiary: patientRef })}`
      : undefined,
    countCoverageChange
  );
  const [policyholderRead, setPolicyholderRead] = useState<PolicyholderRead | undefined>(undefined);
  const relatedPersonIds = relatedPersonList.map((rp) => rp.id).join(',');
  useEffect(() => {
    if (!patientRef) return undefined;
    let current = true;
    const checked = new Set(relatedPersonIds.split(',').filter(Boolean));
    const read = async (): Promise<void> => {
      const policyholders = await readPolicyholderIds(medplum, { reference: patientRef });
      if (current) setPolicyholderRead({ checked, policyholders });
    };
    void read();
    return () => {
      current = false;
    };
  }, [medplum, patientRef, relatedPersonIds, coverageChanges]);

  const actionsFor = useCallback(
    (contact: PatientContactWithRelatedPerson) => {
      const known = contact.relatedPersonId && policyholderRead?.checked.has(contact.relatedPersonId);
      return contactActions(contact, known ? policyholderRead?.policyholders : undefined, permissions);
    },
    [policyholderRead, permissions]
  );

  // allContacts as RelatedPersons + Partners
  const allContacts = useMemo<PatientContactWithRelatedPerson[]>(() => {
    const relatedPersonContacts: PatientContactWithRelatedPerson[] = relatedPersonList.map((rp) => ({
      name: rp.name?.[0],
      relationship: rp.relationship,
      telecom: rp.telecom,
      id: rp.id!,
      relatedPersonId: rp.id!,
    }));

    const partnerContacts: PatientContactWithRelatedPerson[] = partnersList.map((partner) => ({
      ...partner,
      id: partner.organization?.reference || `partner-${Math.random()}`,
    }));

    return [...relatedPersonContacts, ...partnerContacts];
  }, [relatedPersonList, partnersList]);

  const [editingId, setEditingId] = useState<string | null>(null);
  const [formType, setFormType] = useState<'contact' | 'partner' | null>(null);

  const excludedOrganizationIds = useMemo(() => {
    return partnersList.map((contact) => resolveId(contact.organization!)).filter((id): id is string => !!id);
  }, [partnersList]);

  const handleAddContact = useCallback(
    async (contactData: RelatedContactFormData) => {
      await createRelatedPerson(contactData);
    },
    [createRelatedPerson]
  );

  const handleRemoveContact = useCallback(
    async (id: string) => {
      if (!patient || !updatePatient) return;

      const contact = allContacts.find((c) => c.id === id);
      if (!contact) return;

      // Check if it's a RelatedPerson (has relatedPersonId) or a Partner
      if (contact.relatedPersonId) {
        await deleteRelatedPerson(contact.relatedPersonId);
      } else {
        // It's a Partner - find and remove from Patient.contact by matching organization reference
        const organizationRef = contact.organization?.reference;
        if (!organizationRef) return;

        const updatedContacts = (patient.contact || []).filter((c) => {
          // Keep contacts that don't have an organization, or have a different organization reference
          return !c.organization?.reference || c.organization.reference !== organizationRef;
        });

        await updatePatient({
          contact: updatedContacts,
        });
      }
    },
    [patient, updatePatient, allContacts, deleteRelatedPerson]
  );

  const handleEditContact = useCallback(
    (id: string) => {
      setEditingId(editingId === id ? null : id);
    },
    [editingId]
  );

  const handleSaveEdit = useCallback(
    async (contactData: RelatedContactFormData, id: string) => {
      const contact = allContacts.find((c) => c.id === id);
      if (!contact || !contact.relatedPersonId) return;

      await updateRelatedPerson(contact.relatedPersonId, contactData);
      setEditingId(null);
    },
    [allContacts, updateRelatedPerson]
  );

  const handleAddPartner = useCallback(
    async (partner: Organization) => {
      if (!patient || !updatePatient) return;

      const newContact: NonNullable<Patient['contact']>[0] = {
        organization: createReference(partner),
        relationship: [
          {
            text: 'Partner',
          },
        ],
      };

      await updatePatient({
        contact: [...(patient.contact || []), newContact],
      });
    },
    [patient, updatePatient]
  );

  const renderAddForm = useCallback(
    (onClose: () => void) => {
      if (formType === 'partner') {
        return (
          <AddRelatedPartnerForm
            onAdd={handleAddPartner}
            onClose={() => {
              onClose();
              setFormType(null);
            }}
            excludedOrganizationIds={excludedOrganizationIds}
          />
        );
      }
      return (
        <RelatedContactForm
          onSave={handleAddContact}
          onClose={() => {
            onClose();
            setFormType(null);
          }}
        />
      );
    },
    [formType, handleAddContact, handleAddPartner, excludedOrganizationIds]
  );

  const renderEditForm = useCallback(
    (id: string, onClose: () => void) => {
      const contact = allContacts.find((c) => c.id === id);
      if (!contact) return null;

      if (!actionsFor(contact).edit) return null;

      const name = formatHumanName(contact.name) || '';

      const relationshipCodeableConcept = contact.relationship?.[0];
      const relationshipCoding = relationshipCodeableConcept?.coding?.[0];

      return (
        <RelatedContactForm
          key={id}
          initialData={{ name, relationship: relationshipCoding as Coding, telecom: contact.telecom }}
          onSave={(data: RelatedContactFormData) => handleSaveEdit(data, id)}
          onClose={onClose}
        />
      );
    },
    [allContacts, handleSaveEdit, actionsFor]
  );

  const renderAddButton = useCallback(
    ({ showAddForm, onToggle, onClose }: { showAddForm: boolean; onToggle: () => void; onClose: () => void }) => {
      if (showAddForm) {
        return (
          <ActionIcon
            variant="light"
            size="xs"
            onClick={() => {
              setFormType(null);
              onClose();
            }}
            aria-label="Close add form"
          >
            <IconX size={16} />
          </ActionIcon>
        );
      }

      if (!permissions.createRelatedPerson && !permissions.updatePatient) return null;

      return (
        <Menu withinPortal position="bottom-end" shadow="lg" radius="lg">
          <Menu.Target>
            <ActionIcon variant="light" size="xs" aria-label="Add related contact">
              <IconPlus size={16} />
            </ActionIcon>
          </Menu.Target>
          <Menu.Dropdown py="sm">
            {permissions.createRelatedPerson && (
              <Menu.Item
                data-testid="add-related-contact"
                leftSection={<IconUsers size={16} />}
                onClick={() => {
                  setFormType('contact');
                  onToggle();
                }}
              >
                Add New Related Contact
              </Menu.Item>
            )}
            {permissions.updatePatient && (
              <Menu.Item
                data-testid="add-related-partner"
                leftSection={<IconBuildingSkyscraper size={16} />}
                onClick={() => {
                  setFormType('partner');
                  onToggle();
                }}
              >
                Add Related Partner
              </Menu.Item>
            )}
          </Menu.Dropdown>
        </Menu>
      );
    },
    [permissions]
  );

  const renderListSkeleton = () => {
    return (
      <Stack gap={8}>
        {[1, 2, 3].map((i) => (
          <Skeleton key={i} height={40} radius="sm" />
        ))}
      </Stack>
    );
  };

  return (
    <InfoCard
      title="Related contacts"
      addButtonAriaLabel="Add related contact"
      defaultOpen={false}
      renderAddButton={renderAddButton}
      renderList={() => {
        if (relatedPersonListLoading || partnersListLoading) {
          return renderListSkeleton();
        }
        return (
          <RelatedContactList
            items={allContacts}
            actionsFor={actionsFor}
            onRemove={handleRemoveContact}
            onEdit={handleEditContact}
            editingId={editingId}
            renderEditForm={renderEditForm}
          />
        );
      }}
      renderAddForm={renderAddForm}
    />
  );
}
